import { buildSharePointFileName, toDdMmYyyy } from "../../utils/formPdfHelpers";

export const SF009_ELECTRICAL_SHAREPOINT_CONTEXT = "sf-009-electrical-forms-tablets";

export function sanitizeSharePointSegment(value = "") {
  return String(value || "")
    .replace(/[\\/:"*?<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

export function getSharePointDateParts(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const use = isNaN(d.getTime()) ? new Date() : d;
  return {
    year: String(use.getFullYear()),
    month: use.toLocaleString("en-GB", { month: "long" }),
    day: String(use.getDate()).padStart(2, "0"),
  };
}

export function resolveSf009SharePointSuffix({ siteName, contractNumber }) {
  return sanitizeSharePointSegment(siteName) || sanitizeSharePointSegment(contractNumber);
}

// Client convention is "PERSON - SITE - DATE ISSUED", with the SF-009 code kept as a
// trailing segment: all fifteen certificates land in the same SharePoint folder, so
// without it an A can't be told apart from a K.
export function buildSf009SharePointFileName(formCode, fields = {}) {
  return buildSharePointFileName([
    fields.personName,
    resolveSf009SharePointSuffix(fields),
    toDdMmYyyy(fields.formDate),
    `SF-009-${formCode}`,
  ]);
}

export function getSf009SharePointFields(formCode, raw, record, getUserName) {
  const personFromRecord = typeof getUserName === "function" ? getUserName(record) : "";
  switch (formCode) {
    case "A":
      return {
        siteName: raw.site,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: raw.certification?.name || record?.name || personFromRecord,
      };
    case "B":
    case "D":
    case "E":
    case "F":
      return {
        siteName: raw.siteLocation,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "C":
      return {
        siteName: raw.site || raw.siteLocation,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "G":
      return {
        siteName: raw.siteLocation || raw.projectName,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "H":
      return {
        siteName: raw.siteLocation || raw.project,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "I":
      return {
        siteName: raw.siteLocation || raw.projectName,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "J":
      return {
        siteName: raw.siteAddressAndContractNo,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "K":
      return {
        siteName: raw.site,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "L":
    case "M":
    case "O":
      return {
        formDate: raw.date,
        personName: personFromRecord,
      };
    case "N":
      return {
        siteName: raw.siteLocation || raw.projectName,
        contractNumber: raw.contractNumber,
        formDate: raw.date,
        personName: raw.ttslAuthorisedPersonE || raw.finalName || personFromRecord,
      };
    default:
      return { personName: personFromRecord };
  }
}

export function createSf009SharePointUploadConfig(options = {}) {
  return {
    uploadContext: options.uploadContext || SF009_ELECTRICAL_SHAREPOINT_CONTEXT,
    onSuccess: options.onSuccess,
    onError: options.onError,
  };
}

export async function postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey }) {
  try {
    if (setUploadingKey && uploadKey != null) setUploadingKey(uploadKey);
    const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const dataUri = doc.output("datauristring");
    const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      body: JSON.stringify({
        fileName,
        fileBase64: dataUri,
        uploadContext: uploadConfig.uploadContext,
      }),
    });
    if (res.status !== 204 && !res.ok) {
      let msg = "SharePoint upload failed";
      try {
        const out = await res.json();
        msg = out?.message || msg;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    if (uploadConfig.onSuccess) uploadConfig.onSuccess();
    return true;
  } catch (err) {
    if (uploadConfig.onError) uploadConfig.onError(err);
    throw err;
  } finally {
    if (setUploadingKey) setUploadingKey(null);
  }
}
